import { Plus, SquareArrowOutUpRight, Trash } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { AnimateSpin } from '@/components/AnimateSpin';
import { ProductImageFallback } from '@/components/ProductImageFallback';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { ROUTE_PATHS } from '@/config/constants';
import { useAuth } from '@/contexts/AuthContext';
import { useCreateMerchandisingCollectionSliceProduct } from '@/hooks/useCreateMerchandisingCollectionSliceProduct';
import { useRemoveMerchandisingCollectionSliceProduct } from '@/hooks/useRemoveMerchandisingCollectionSliceProduct';
import { cn, priceFormatterV2 } from '@/lib/utils';
import { Product } from '@/services/ProductsService';

interface ProductCard {
  product: Product;
  isSaving: boolean;
  onSaving: (value: boolean) => void;
  addToFirstPosition: boolean;
}

export function ProductCard({
  product,
  isSaving,
  onSaving,
  addToFirstPosition,
}: ProductCard) {
  const [imageError, setImageError] = useState(false);

  const { user } = useAuth();

  const { collectionId, sliceId } = useParams();

  const { t } = useTranslation('translation', {
    keyPrefix:
      'pages.MerchandisingCollections.pages.Slice.components.ProductSelect.components.ProductCard',
  });

  const productAlreadyAdded = product.merchandisingCollectionSlices
    ?.split(',')
    .map((s) => s.trim())
    .includes(String(sliceId ?? ''));

  const { createSliceProduct } = useCreateMerchandisingCollectionSliceProduct();
  const { removeSliceProduct } = useRemoveMerchandisingCollectionSliceProduct();

  async function handleSave() {
    if (isSaving) {
      return;
    }

    onSaving(true);

    try {
      await createSliceProduct({
        collectionId: Number(collectionId),
        sliceId: Number(sliceId),
        dto: {
          productId: product.productId,
          addToFirstPosition,
        },
      });

      toast.success(t('toast.addProductSuccess'));
    } catch {
      toast.error(t('toast.addProductError'));
    } finally {
      onSaving(false);
    }
  }

  async function handleRemoveProduct() {
    if (isSaving) {
      return;
    }

    onSaving(true);

    try {
      await removeSliceProduct({
        collectionId: Number(collectionId),
        sliceId: Number(sliceId),
        productId: product.productId,
      });

      toast.success(t('toast.removeProductSuccess'));
    } catch {
      toast.error(t('toast.removeProductError'));
    } finally {
      onSaving(false);
    }
  }

  return (
    <Card
      className={cn(
        'relative',
        productAlreadyAdded && 'bg-green-50 ring-2 ring-green-400',
      )}
    >
      <div className="absolute right-4 top-4 flex items-center gap-1">
        <Button asChild size="sm">
          <Link
            to={ROUTE_PATHS.VISION_PRODUCT.replace(
              ':productId',
              String(product.id),
            )}
            target="_blank"
          >
            <SquareArrowOutUpRight className="h-3 w-3" />
          </Link>
        </Button>

        {productAlreadyAdded && (
          <Button
            variant="destructive"
            size="sm"
            disabled={isSaving}
            onClick={handleRemoveProduct}
          >
            {isSaving ? (
              <AnimateSpin className="h-3 w-3" />
            ) : (
              <Trash className="h-3 w-3" />
            )}
          </Button>
        )}

        {!productAlreadyAdded && (
          <Button size="sm" disabled={isSaving} onClick={handleSave}>
            {isSaving ? (
              <AnimateSpin className="h-3 w-3" />
            ) : (
              <Plus className="h-3 w-3" />
            )}
          </Button>
        )}
      </div>

      <CardContent className="p-4">
        <div className="flex flex-col space-y-3">
          {imageError ? (
            <ProductImageFallback
              showMessage={false}
              className="mx-auto h-60 w-full rounded-md object-cover lg:h-40 xl:h-60"
            />
          ) : (
            <img
              src={product.image1}
              alt={product.name}
              className="mx-auto h-60 w-full rounded-md object-cover lg:h-40 xl:h-60"
              onError={() => setImageError(true)}
            />
          )}

          <div className="space-y-2">
            <h4 className="line-clamp-2 text-sm font-medium">{product.name}</h4>
            <p className="text-xs text-muted-foreground">
              {product.visionCategoryName}
            </p>

            <div className="mt-1 flex items-center space-x-2">
              {parseFloat(product.price) > parseFloat(product.salePrice) && (
                <span className="text-sm line-through opacity-50">
                  {priceFormatterV2(
                    parseFloat(product.price),
                    user!.customer.language,
                    user!.customer.platform,
                  )}
                </span>
              )}

              <span
                className={cn(
                  'text-sm font-semibold',
                  parseFloat(product.price) > parseFloat(product.salePrice) &&
                    'text-red-400',
                )}
              >
                {priceFormatterV2(
                  parseFloat(product.salePrice),
                  user!.customer.language,
                  user!.customer.platform,
                )}
              </span>
            </div>

            <Badge
              variant={product.stock ? 'default' : 'secondary'}
              className="w-fit text-xs"
            >
              {product.stock ? t('inStock') : t('outOfStock')}
            </Badge>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
